import React from "react";
import { Link } from "react-router-dom";
import {
  FileText, HelpCircle, GitBranch, Scale, Wrench, Video, Download, ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

/* ────────────────────────────── PageHeader ────────────────────────────── */
export function PageHeader({ title, subtitle, eyebrow, breadcrumbs, children, testid }) {
  return (
    <div className="mb-8 kp-in" data-testid={testid}>
      {breadcrumbs?.length > 0 && (
        <nav className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground mb-3">
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="w-3 h-3 opacity-50" />}
              {b.to ? (
                <Link to={b.to} className="kp-link hover:text-foreground transition-colors">{b.label}</Link>
              ) : (
                <span className="text-foreground">{b.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      )}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">
              {eyebrow}
            </p>
          )}
          <h1 className="font-display text-3xl md:text-[42px] leading-[1.05] text-foreground">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm md:text-base text-muted-foreground mt-3 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
        {children && <div className="shrink-0 flex items-center gap-2 flex-wrap">{children}</div>}
      </div>
      <div className="kp-rule mt-6" />
    </div>
  );
}

/* ────────────────────────────── SectionLabel ────────────────────────────── */
export function SectionLabel({ children, icon: Icon, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {children}
      </p>
      {action}
    </div>
  );
}

/* ────────────────────────────── EmptyState ────────────────────────────── */
export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-20 rounded-2xl border border-dashed border-border bg-card kp-in">
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-secondary/60 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-primary" />
        </div>
      )}
      <p className="font-display text-xl text-foreground">{title}</p>
      {hint && <p className="text-sm text-muted-foreground mt-1.5 max-w-sm">{hint}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ────────────────────────────── TypeBadge ──────────────────────────────
   Editorial content-type chips with icon + label for scannability.
*/
const KIND_META = {
  sop:             { icon: FileText,   label: "SOP",           tone: "text-primary bg-secondary/70 border-primary/15" },
  faq:             { icon: HelpCircle, label: "FAQ",           tone: "text-primary bg-accent/40 border-accent" },
  workflow:        { icon: GitBranch,  label: "Workflow",      tone: "text-primary bg-accent/25 border-accent/70" },
  business_rule:   { icon: Scale,      label: "Business Rule", tone: "text-[#7a5a12] bg-[#fdf2c9] border-[#f0d98c]" },
  troubleshooting: { icon: Wrench,     label: "Troubleshoot",  tone: "text-[#a23a2a] bg-[#fbe0d9] border-[#f0b8a8]" },
  video:           { icon: Video,      label: "Video",         tone: "text-[#25604d] bg-[#d9f2e5] border-[#a5dbc0]" },
  download:        { icon: Download,   label: "Download",      tone: "text-foreground bg-muted border-border" },
};

export function TypeBadge({ type, size = "sm" }) {
  const m = KIND_META[type] || KIND_META.download;
  const Icon = m.icon;
  const px = size === "lg" ? "px-2.5 py-1 text-[11px]" : "px-2 py-0.5 text-[10px]";
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border font-medium uppercase tracking-wider ${px} ${m.tone}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export const CONTENT_TYPES = KIND_META;

/* ────────────────────────────── StatusPill ────────────────────────────── */
const STATUS_MAP = {
  uploaded:  "text-[#25604d] bg-[#d9f2e5] border-[#a5dbc0]",
  ready:     "text-[#25604d] bg-[#d9f2e5] border-[#a5dbc0]",
  missing:   "text-[#a23a2a] bg-[#fbe0d9] border-[#f0b8a8]",
  blocked:   "text-[#a23a2a] bg-[#fbe0d9] border-[#f0b8a8]",
  active:    "text-[#25604d] bg-[#d9f2e5] border-[#a5dbc0]",
  inactive:  "text-muted-foreground bg-muted border-border",
  draft:     "text-muted-foreground bg-muted border-border",
  submitted: "text-[#7a5a12] bg-[#fdf2c9] border-[#f0d98c]",
  published: "text-[#25604d] bg-[#d9f2e5] border-[#a5dbc0]",
  rejected:  "text-[#a23a2a] bg-[#fbe0d9] border-[#f0b8a8]",
};

export function StatusPill({ status }) {
  return (
    <Badge variant="outline" className={`capitalize font-medium ${STATUS_MAP[status] || ""}`}>
      {status}
    </Badge>
  );
}
